/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { FastField, Field, FormikProps } from "formik";
import { addDays, addYears } from "office-ui-fabric-react/lib/utilities/dateMath/DateMath";

import { ITag, Label, Stack, Text } from "@fluentui/react";

import { SidePanel } from "Components/SidePanel/SidePanel";
import { getSchoolData } from "Dataflows/School/SchoolDataAction";
import {
  getStateProvince,
  getStateProvinceSchool,
  getStateProvinceShipping,
} from "Dataflows/StateProvince/StateProvinceAction";

import { languages } from "../../Constants/Languages";
import {
  genders,
  namesLocalized,
  pronounPreferences,
  schoolDegreeLevels,
  schoolDegreePrograms,
  shirtFitTypes,
  shirtSizes,
  titles,
  urlPrefix,
} from "../../Constants/ProfileConstants";
import { IFormField } from "../../Interfaces/IFormField";
import { IUserProfile } from "../../Interfaces/UserProfile/IUserProfile";
import { IUserProfileSchool } from "../../Interfaces/UserProfile/IUserProfileSchool";
import { IUserProfileSocialNetwork } from "../../Interfaces/UserProfile/IUserProfileSocialNetwork";
import FormikDatePicker from "../Common/DatePicker/FormikDatePicker";
import FormikDropdown from "../Common/Dropdown/FormikDropdown";
import FormikMultiDropdown from "../Common/Dropdown/FormikMultiDropdown";
import FormikErrorMessage from "../Common/FormikErrorMessage/FormikErrorMessage";
import FormikPicker from "../Common/Picker";
import FormikTextField from "../Common/TextField/FormikTextField";
import FormikToggle from "../Common/Toggle";
import { IStudentEditProfileFormValues } from "./Interfaces/IStudentEditProfileFormValues";
import { IStudentEditProfileProps } from "./Interfaces/IStudentEditProfileProps";
import {
  datePickerFieldStyles,
  formSectionTitleStyles,
  horizontalFormFieldsStackTokens,
  isSocialMediaPrivateLabelStyles,
  sectionSubtitleStyles,
  shippingSameLabelStyles,
  toggleFieldStyles,
  FieldBlock,
  FieldSet,
  IsSocialMediaPrivate,
  IsSocialMediaPrivateRow,
  ShippingSameRow,
  ShippingSameSwitch,
} from "./StudentEdit.styles";

const MIN_DAYS = 180;

const formSectionsFields = [
  {
    title: "Personal Information",
    fields: [
      {
        name: "firstName",
        label: "First Name",
      },
      {
        name: "lastName",
        label: "Last Name",
      },
      {
        name: "localizedFirstName",
        label: "Localized First Name",
      },
      {
        name: "localizedLastName",
        label: "Localized Last Name",
      },
      {
        name: "awardNameLocalized",
        label: "Choose print name as either",
        type: "select",
        options: namesLocalized,
        placeholder: "Select localization option",
      },
      {
        name: "screenNameLocalized",
        label: "Choose display name as either",
        type: "select",
        options: namesLocalized,
        placeholder: "Select localization option",
      },
      {
        name: "genderName",
        label: "Gender",
        type: "select",
        options: genders,
        placeholder: "Select gender",
      },
      {
        name: "genderPronounPreferenceName",
        label: "Pronoun",
        type: "select",
        options: pronounPreferences,
        placeholder: "Select preferred pronoun",
      },
      {
        name: "titleName",
        label: "Title",
        type: "select",
        options: titles,
        placeholder: "Select preferred title",
      },
      {
        name: "dateOfBirth",
        label: "Date of Birth",
        type: "date",
      },
      {
        name: "primaryEmail",
        label: "Site Email",
        disabled: true,
      },
      {
        name: "secondaryEmail",
        label: "Preferred Email",
      },
      {
        name: "studentPartnerEmail",
        label: "Student Partner Email",
        disabled: true,
      },
      {
        name: "languages",
        label: "Languages",
        type: "multiSelect",
        options: languages,
        placeholder: "Select languages",
      },
      {
        name: "shirtFitType",
        label: "Shirt Fit Type",
        type: "select",
        options: shirtFitTypes,
        placeholder: "Select shirt fit",
      },
      {
        name: "shirtSize",
        label: "Shirt Size",
        type: "select",
        options: shirtSizes,
        placeholder: "Select shirt size",
      },
      {
        name: "userProfileProgramLanguage",
        label: "Preferred Programming Language",
        type: "multiSelect",
        placeholder: "Select Preferred Programming Language",
      },
    ] as IFormField[],
  },
  {
    title: "Biography",
    fields: [
      {
        name: "headline",
        label: "Headline",
        type: "headline",
      },
      {
        name: "biography",
        label: "Biography",
        type: "biography",
      },
    ] as IFormField[],
  },
  {
    title: "Address",
    fields: [
      {
        name: "address1",
        label: "Address 1",
      },
      {
        name: "address2",
        label: "Address 2",
      },
      {
        name: "addressCity",
        label: "City",
      },
      {
        name: "addressPostCode",
        label: "Post Code",
      },
      {
        name: "addressCountryOrRegionName",
        label: "Country or Region",
        type: "countryRegion",
        placeholder: "Select country or region",
      },
      {
        name: "addressStateOrProvince",
        label: "State of Province",
        type: "stateProvince",
        placeholder: "Select state of province",
      },
      {
        name: "primaryPhoneCountryOrRegionName",
        label: "Primary Country Code",
        type: "country",
        placeholder: "Select country code",
      },
      {
        name: "primaryPhone",
        label: "Primary Phone number",
      },
    ] as IFormField[],
  },
  {
    title: "Shipping Address",
    fields: [
      {
        name: "shippingAddress1",
        label: "Address 1",
      },
      {
        name: "shippingAddress2",
        label: "Address 2",
      },
      {
        name: "shippingCity",
        label: "City",
      },
      {
        name: "shippingPostCode",
        label: "Post Code",
      },
      {
        name: "shippingCountryOrRegionName",
        label: "Country or Region",
        type: "shippingCountryRegion",
        placeholder: "Select country or region",
      },
      {
        name: "shippingStateOrProvince",
        label: "State of Province",
        type: "shippingStateProvince",
        placeholder: "Select shipping state of province",
      },
      {
        name: "shippingPhoneCountryOrRegionName",
        label: "Shipping Country Code",
        type: "shippingcountry",
        placeholder: "Select country code",
      },
      {
        name: "shippingPhoneNumber",
        label: "Phone number",
      },
    ] as IFormField[],
  },
];

/**
 * Functional component descibing the edit user profile form.
 * @param {FormikProps} props FormikProps.
 * @returns {React.ReactElement} EditUserProfileForm Component.
 */
const StudentEditProfile = (
  props: FormikProps<IStudentEditProfileFormValues> &
    IStudentEditProfileProps & {
      selectedUserProfile: IUserProfile;
    }
): React.ReactElement => {
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    handleBlur,
    handleChange,
    handleSubmit,
    isSelectedUserProfileUpdating,
    setFieldValue,
    stateProvinceOptionList,
    countryRegionOptionList,
    countryRegionPhoneOptionList,
    stateProvinceShippingOptionList,
    stateProvinceSchoolOptionList,
    schoolOptionList,
    socialNetworkOptionList,
    programLanguageOptionList,
    values,
    canEditUser,
    updateProfileError,
    handlePanelClose,
  } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    if (isSelectedUserProfileUpdating) {
      setIsUpdating(true);
    } else {
      if (isUpdating && updateProfileError === "") {
        // close panel on successful update of profile
        handlePanelClose();
        setIsUpdating(false);
      }
    }
  }, [isSelectedUserProfileUpdating]);

  // Shipping fields should be clean it if the toggle is ON
  if (!!values.shippingSameAsAddress) {
    values.shippingAddress1 = "";
    values.shippingAddress2 = "";
    values.shippingCity = "";
    values.shippingPostCode = "";
    values.shippingCountryOrRegionName = "";
    values.shippingStateOrProvince = "";
    values.shippingPhoneCountryOrRegionName = "";
    values.shippingPhoneNumber = "";
  }

  // Program language options should be updated from the api
  if (!!programLanguageOptionList) {
    const indexPersonalInformation = formSectionsFields.findIndex((obj) => {
      return obj.title === "Personal Information";
    });
    const indexUserProfileProgramLanguage = formSectionsFields[
      indexPersonalInformation
    ].fields.findIndex((obj) => {
      return obj.name === "userProfileProgramLanguage";
    });
    formSectionsFields[indexPersonalInformation].fields[indexUserProfileProgramLanguage].options =
      programLanguageOptionList;
  }

  const handleOnSave = () => {
    handleSubmit();
  };

  return (
    <form onSubmit={handleSubmit}>
      <SidePanel
        isOpen={props.isPanelOpen}
        title="Edit Profile Details"
        saveButtonLabel="Save"
        error={updateProfileError}
        disableActions={isSelectedUserProfileUpdating}
        onDismiss={handlePanelClose}
        onSave={handleOnSave}
      >
        <FieldSet disabled={isSelectedUserProfileUpdating}>
          {formSectionsFields.map((section) => (
            <div key={section.title}>
              <Text block styles={formSectionTitleStyles} key={`${section.title}-title`}>
                {section.title}
              </Text>
              {section.title == "Shipping Address" && (
                <ShippingSameSwitch>
                  <ShippingSameRow>
                    <Label styles={shippingSameLabelStyles}>
                      Shipping Address same as Primary Address?
                    </Label>
                    <FastField
                      name="shippingSameAsAddress"
                      onChange={(_, checked): void => {
                        setFieldValue("shippingSameAsAddress", checked);
                      }}
                      component={FormikToggle}
                      stackWidth={40}
                      styles={toggleFieldStyles}
                      disabled={!canEditUser}
                    />
                  </ShippingSameRow>
                </ShippingSameSwitch>
              )}
              <Stack
                horizontal
                wrap
                tokens={horizontalFormFieldsStackTokens}
                key={`${section.title}-stack`}
              >
                {section.fields.map((field) => (
                  <FieldBlock key={`${section.title}-${field.name}`}>
                    <Label key={`${field.label}-label`}>{field.label}</Label>
                    {field.type === "date" && (
                      <FastField
                        name={field.name}
                        onSelectDate={(date: Date): void => {
                          setFieldValue(field.name, date);
                        }}
                        component={FormikDatePicker}
                        showGoToToday={false}
                        key={field.name}
                        disabled={!canEditUser}
                      />
                    )}
                    {field.type === "multiSelect" && (
                      <FastField
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, item: any): void => {
                          const fieldProps = (values as { [key: string]: any })[field.name];
                          if (item.selected) {
                            setFieldValue(field.name, [...fieldProps, item.key]);
                          } else {
                            const currIndex = fieldProps.indexOf(item.key);
                            setFieldValue(field.name, [
                              ...fieldProps.slice(0, currIndex),
                              ...fieldProps.slice(currIndex + 1),
                            ]);
                          }
                        }}
                        options={field.options}
                        placeholder={field.placeholder}
                        component={FormikMultiDropdown}
                        key={field.name}
                        disabled={!canEditUser}
                      />
                    )}
                    {field.type === "select" && (
                      <FastField
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, value: any): void => {
                          setFieldValue(field.name, value.key);
                        }}
                        options={field.options}
                        component={FormikDropdown}
                        key={field.name}
                        disabled={!canEditUser}
                      />
                    )}
                    {field.type === "country" && (
                      <FastField
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, item: any): void => {
                          setFieldValue("primaryPhoneCountryOrRegionId", item.id);
                          setFieldValue(field.name, item.key);
                          setFieldValue("primaryPhoneCountryOrRegionTapiCode", item.tapi);
                        }}
                        placeholder={field.placeholder}
                        options={countryRegionPhoneOptionList}
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                    )}
                    {field.type === "shippingcountry" && (
                      <Field
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, item: any): void => {
                          setFieldValue("shippingPhoneCountryOrRegionId", item.id);
                          setFieldValue(field.name, item.key);
                          setFieldValue("shippingPhoneCountryOrRegionTapiCode", item.tapi);
                        }}
                        placeholder={field.placeholder}
                        options={countryRegionPhoneOptionList}
                        component={FormikDropdown}
                        disabled={!!values.shippingSameAsAddress || !canEditUser}
                      />
                    )}
                    {field.type === "countryRegion" && (
                      <FastField
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, item: any): void => {
                          props.values.addressStateOrProvince = "";
                          setFieldValue(field.name, item.key);
                          dispatch(getStateProvince(item.key));
                        }}
                        placeholder={field.placeholder}
                        options={countryRegionOptionList}
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                    )}
                    {field.type === "shippingCountryRegion" && (
                      <Field
                        name={field.name}
                        onChange={(_, item: any): void => {
                          props.values.shippingStateOrProvince = "";
                          setFieldValue(field.name, item.key);
                          dispatch(getStateProvinceShipping(item.key));
                        }}
                        placeholder={field.placeholder}
                        options={countryRegionOptionList}
                        component={FormikDropdown}
                        disabled={!!values.shippingSameAsAddress || !canEditUser}
                      />
                    )}
                    {field.type === "stateProvince" && (
                      <Field
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, item: any): void => {
                          setFieldValue(field.name, item.key);
                        }}
                        placeholder={field.placeholder}
                        options={stateProvinceOptionList}
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                    )}
                    {field.type === "shippingStateProvince" && (
                      <Field
                        name={field.name}
                        onChange={(_: React.FormEvent<HTMLInputElement>, item: any): void => {
                          setFieldValue(field.name, item.key);
                        }}
                        placeholder={field.placeholder}
                        options={stateProvinceShippingOptionList}
                        component={FormikDropdown}
                        disabled={!!values.shippingSameAsAddress || !canEditUser}
                      />
                    )}
                    {![
                      "date",
                      "multiSelect",
                      "select",
                      "country",
                      "shippingcountry",
                      "countryRegion",
                      "stateProvince",
                      "shippingCountryRegion",
                      "shippingStateProvince",
                      "headline",
                      "biography",
                    ].includes(field.type || "") && (
                      <div>
                        <Field
                          type="text"
                          name={field.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={
                            (field.disabled ? field.disabled : false) ||
                            (section.title == "Shipping Address" &&
                              !!values.shippingSameAsAddress) ||
                            !canEditUser
                          }
                          key={field.name}
                        />
                        <FormikErrorMessage name={field.name} key={`${field.name}-error`} />
                      </div>
                    )}
                    {["headline"].includes(field.type || "") && (
                      <div>
                        <Field
                          as="textarea"
                          rows={4}
                          name={field.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          key={field.name}
                          disabled={!canEditUser}
                        />
                        <FormikErrorMessage name={field.name} key={`${field.name}-error`} />
                      </div>
                    )}
                    {["biography"].includes(field.type || "") && (
                      <div>
                        <Field
                          as="textarea"
                          rows={4}
                          name={field.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          key={field.name}
                        />
                        <FormikErrorMessage name={field.name} key={`${field.name}-error`} />
                      </div>
                    )}
                  </FieldBlock>
                ))}
              </Stack>
            </div>
          ))}

          <div key="userProfileSchool">
            <Text block styles={formSectionTitleStyles} key="userProfileSchool-title">
              School/University
            </Text>
            {values.userProfileSchool.map((profileSchool: IUserProfileSchool, index: number) => {
              const userSchool = values.userProfileSchool[index];

              const emptyCountryName = userSchool && userSchool.SchoolCountryRegionName === "";
              const emptyStateName = userSchool && userSchool.schoolStateProvinceName === "";
              const emptyStateList =
                Array.isArray(stateProvinceSchoolOptionList) &&
                stateProvinceSchoolOptionList.length < 1;
              return (
                <div key={`userProfileSchool-${profileSchool.id}`}>
                  <Text
                    block
                    styles={sectionSubtitleStyles}
                    key={`Profile School ID: ${profileSchool.id}`}
                  >{`Profile School ID: ${profileSchool.id}`}</Text>
                  <Stack
                    key="profileSchool"
                    horizontal
                    wrap
                    tokens={horizontalFormFieldsStackTokens}
                  >
                    <FieldBlock key="userProfileSchool-schoolCountryRegionName">
                      <Label>Country or Region</Label>
                      <FastField
                        type="text"
                        name={`userProfileSchool[${index}].schoolCountryRegionName`}
                        onChange={(_: React.FormEvent<HTMLInputElement>, value: any): void => {
                          dispatch(getStateProvinceSchool(value.key));
                          dispatch(getSchoolData(value.key));

                          setFieldValue(
                            `userProfileSchool[${index}].schoolCountryRegionName`,
                            value.key
                          );

                          /**
                           * TODO: BUG: Need to trigger the SchoolPicker to rerender here so that it can pick up that
                           * there is not a selected school and clear out the currently selected school.
                           */
                          setFieldValue(`userProfileSchool[${index}].schoolStateProvinceName`, "");
                          setFieldValue(`userProfileSchool[${index}].schoolName`, "");
                        }}
                        options={countryRegionOptionList}
                        placeholder="select school country or region"
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                      {props.errors[`userProfileSchool[${index}].schoolCountryRegionName`] && (
                        <FormikErrorMessage
                          name={`userProfileSchool[${index}].schoolCountryRegionName`}
                        />
                      )}
                    </FieldBlock>
                    <FieldBlock key="userProfileSchool-schoolStateProvinceName">
                      <Label>State or Province</Label>
                      <Field
                        type="text"
                        name={`userProfileSchool[${index}].schoolStateProvinceName`}
                        onChange={(_: React.FormEvent<HTMLInputElement>, value: any): void => {
                          setFieldValue(
                            `userProfileSchool[${index}].schoolStateProvinceName`,
                            value.key
                          );
                        }}
                        placeholder="select state of province"
                        options={stateProvinceSchoolOptionList}
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                    </FieldBlock>
                    <FieldBlock key="userProfileSchool-schoolName">
                      <Label>School name</Label>
                      <Field
                        disabled={
                          emptyCountryName || (emptyStateName && !emptyStateList) || !canEditUser
                        }
                        name={`userProfileSchool[${index}].schoolName`}
                        removeButtonAriaLabel="Remove"
                        pickerSuggestionsProps={{
                          suggestionHeaderText: "Suggested Schools",
                          noResultFoundText: "No Schools Found",
                        }}
                        itemLimit={1}
                        resolveDelay={500}
                        itemProps={{
                          onKeyPress: (e): void => {
                            e.preventIcon();
                          },
                        }}
                        component={FormikPicker}
                        allowTypedInputItemResult={true}
                        suggestedItems={schoolOptionList}
                        placeholder="Select Academic Institution"
                        onChange={(items?: ITag[] | undefined): void => {
                          const fieldName = `userProfileSchool[${index}].schoolName`;
                          if (!items || items.length === 0) {
                            setFieldValue(fieldName, "");
                            return;
                          }

                          setFieldValue(fieldName, items[`${index}`].name);
                        }}
                        stackWidth="auto"
                        selectedItems={
                          values.userProfileSchool[index]?.schoolName === null ||
                          values.userProfileSchool[index]?.schoolName === ""
                            ? null
                            : values.userProfileSchool.map((x) => {
                                return {
                                  name: x.schoolName,
                                };
                              })
                        }
                      />
                      {props.errors[`userProfileSchool[${index}].schoolName`] && (
                        <FormikErrorMessage name={`userProfileSchool[${index}].schoolName`} />
                      )}
                    </FieldBlock>
                    <FieldBlock key="userProfileSchool-schoolDegreeProgramName">
                      <Label>Degree program</Label>
                      <Field
                        name={`userProfileSchool[${index}].schoolDegreeProgramName`}
                        onChange={(_: React.FormEvent<HTMLInputElement>, value: any): void => {
                          setFieldValue(
                            `userProfileSchool[${index}].schoolDegreeProgramName`,
                            value.key
                          );
                        }}
                        options={schoolDegreePrograms}
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                    </FieldBlock>
                    <FieldBlock key="userProfileSchool-schoolDegreeLevelName">
                      <Label>Degree level name</Label>
                      <FastField
                        name={`userProfileSchool[${index}].schoolDegreeLevelName`}
                        onChange={(_: React.FormEvent<HTMLInputElement>, value: any): void => {
                          setFieldValue(
                            `userProfileSchool[${index}].schoolDegreeLevelName`,
                            value.key
                          );
                        }}
                        placeholder="Select degree level"
                        options={schoolDegreeLevels}
                        component={FormikDropdown}
                        disabled={!canEditUser}
                      />
                    </FieldBlock>
                    <FieldBlock key="userProfileSchool-expectedGraduationDate">
                      <Label>Expected graduation date</Label>
                      <FastField
                        name={`userProfileSchool[${index}].expectedGraduationDate`}
                        onSelectDate={(date: Date): void => {
                          setFieldValue(`userProfileSchool[${index}].expectedGraduationDate`, date);
                        }}
                        endDate={addYears(new Date(Date.now()), 5)}
                        initialPickerDate={addDays(new Date(Date.now()), MIN_DAYS)}
                        component={FormikDatePicker}
                        placeholder="Select graduation date"
                        styles={datePickerFieldStyles}
                        disabled={!canEditUser}
                      />
                    </FieldBlock>
                  </Stack>
                </div>
              );
            })}
          </div>

          <div key="userProfileSocialNetwork">
            <Text block styles={formSectionTitleStyles} key="userProfileSocialNetwork-title">
              Social Networks
            </Text>
            {values.userProfileSocialNetwork.map(
              (profileSocialNetwork: IUserProfileSocialNetwork, index: number) => {
                return (
                  <div key={`userProfileSocialNetwork-${profileSocialNetwork.id}`}>
                    <Text
                      block
                      styles={sectionSubtitleStyles}
                      key={`Profile Social Network ID: ${profileSocialNetwork.id}`}
                    >{`Profile Social Network ID: ${profileSocialNetwork.id}`}</Text>
                    <IsSocialMediaPrivate>
                      <IsSocialMediaPrivateRow>
                        <Label styles={isSocialMediaPrivateLabelStyles}>
                          Make social network private?
                        </Label>
                        <FastField
                          name={`userProfileSocialNetwork[${index}].isPrivate`}
                          onChange={(_, checked): void => {
                            setFieldValue(`userProfileSocialNetwork[${index}].isPrivate`, checked);
                          }}
                          component={FormikToggle}
                          stackWidth={40}
                          styles={toggleFieldStyles}
                          disabled={!canEditUser}
                        />
                      </IsSocialMediaPrivateRow>
                    </IsSocialMediaPrivate>
                    <Stack
                      key="profileSocialNetwork"
                      horizontal
                      wrap
                      tokens={horizontalFormFieldsStackTokens}
                    >
                      <FieldBlock key="userProfileSocialNetwork-socialNetworkName">
                        <Label>Social Network</Label>
                        <Field
                          type="text"
                          name={`userProfileSocialNetwork[${index}].socialNetworkName`}
                          onChange={(
                            _: React.FormEvent<HTMLInputElement>,
                            value: { key: string; text: string; urlPrefix: string }
                          ): void => {
                            setFieldValue(
                              `userProfileSocialNetwork[${index}].socialNetworkName`,
                              value.key
                            );
                            setFieldValue(
                              `userProfileSocialNetwork[${index}].socialNetworkUrlPrefix`,
                              value.urlPrefix || urlPrefix
                            );
                          }}
                          placeholder="select social network"
                          options={socialNetworkOptionList}
                          component={FormikDropdown}
                          disabled={!canEditUser}
                        />
                      </FieldBlock>
                      <FieldBlock key="userProfileSocialNetwork-handle">
                        <Label>URL</Label>
                        <Field
                          component={FormikTextField}
                          name={`userProfileSocialNetwork[${index}].handle`}
                          prefix={
                            values.userProfileSocialNetwork[index].socialNetworkUrlPrefix ||
                            urlPrefix
                          }
                          onChange={(_: React.FormEvent<HTMLInputElement>, value: any): void => {
                            setFieldValue(`userProfileSocialNetwork[${index}].handle`, value);
                          }}
                          disabled={!canEditUser}
                        />
                      </FieldBlock>
                    </Stack>
                  </div>
                );
              }
            )}
          </div>
        </FieldSet>
      </SidePanel>
    </form>
  );
};

export default StudentEditProfile;
